// frontend/src/pages/ForgotPassword.tsx

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    try {
      setIsLoading(true);
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (error) {
      // Backend intentionally returns success regardless, to avoid revealing
      // which emails have accounts. Show the same confirmation either way.
      setSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-apple-blue-50 to-white py-12 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Zap className="w-8 h-8 text-apple-blue-500" />
          <span className="text-2xl font-semibold">SLE DEX</span>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-3xl">Forgot Password</CardTitle>
          </CardHeader>

          <CardContent>
            {sent ? (
              <div className="text-center space-y-4">
                <p className="text-apple-gray-700">
                  If an account exists for <strong>{email}</strong>, a password reset link
                  has been sent. Please check your inbox (and your spam folder).
                </p>
                <Link
                  to="/login"
                  className="inline-block text-apple-blue-500 hover:text-apple-blue-600 font-medium"
                >
                  Back to login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <p className="text-sm text-apple-gray-600">
                  Enter your email address and we'll send you a link to reset your password.
                </p>

                <Input
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />

                <Button type="submit" variant="primary" className="w-full" isLoading={isLoading}>
                  Send reset link
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm text-apple-blue-500 hover:text-apple-blue-600"
                  >
                    Back to login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
